// =====================================================================
// src/lib/ibge.js — países e municípios do IBGE (0070), sem DOM
//
// A API da FNRH pede o país em código (tabela do IBGE, ISO alpha-2) e o
// município em código IBGE de 7 dígitos (docs/fnrh-api.md). As listas vêm
// de dados/ibge/ (LEIAME.md) — as MESMAS que a migration 0070 carrega no
// banco, que é quem recusa código fora delas:
//   · os 193 países vão no bundle (paises_ibge.js, gerado);
//   · os 5.571 municípios ficam num arquivo estático, baixado uma vez só,
//     quando alguém escolhe "mora no Brasil" — não pesam na abertura da
//     tela de quem não precisa deles.
// =====================================================================
import { PAISES_IBGE } from './paises_ibge.js';

export const BRASIL = 'BR';
export const URL_MUNICIPIOS = '/dados/ibge/municipios.json';
export const UFS = Object.freeze(['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']);

const PAIS = new Map(PAISES_IBGE);
export const ehPais = id => PAIS.has(String(id || ''));

// O nome do país. Em inglês (a página do hóspede é bilíngue), o nome que o
// PRÓPRIO navegador tem para a sigla (Intl.DisplayNames) — não uma
// tradução nossa; sem ele, o nome do IBGE.
function nomeEmIngles(id) {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(id) || ''; } catch (_) { return ''; }
}
export function nomeDoPais(id, idioma = 'pt') {
  const s = String(id || '').toUpperCase();
  if (!PAIS.has(s)) return '';
  if (idioma === 'en') { const n = nomeEmIngles(s); if (n && n !== s) return n; }
  return PAIS.get(s);
}

// As opções do seletor: o Brasil primeiro (é a maioria dos hóspedes), o
// resto em ordem alfabética no idioma da tela.
export function opcoesDePaises(idioma = 'pt') {
  const colar = new Intl.Collator(idioma === 'en' ? 'en' : 'pt-BR');
  const resto = PAISES_IBGE.filter(([id]) => id !== BRASIL).map(([id]) => [id, nomeDoPais(id, idioma)]).sort((a, b) => colar.compare(a[1], b[1]));
  return [[BRASIL, nomeDoPais(BRASIL, idioma)], ...resto];
}

// ── os municípios ────────────────────────────────────────────────────
// O arquivo é [[id, nome, uf], ...]. Vira dois índices: por id (o nome
// na ficha) e por UF (o seletor, em ordem alfabética).
export function indexarMunicipios(lista) {
  const colar = new Intl.Collator('pt-BR');
  const porId = new Map(); const porUF = new Map();
  for (const [id, nome, uf] of lista || []) {
    const m = { id: Number(id), nome, uf };
    porId.set(m.id, m);
    if (!porUF.has(uf)) porUF.set(uf, []);
    porUF.get(uf).push(m);
  }
  for (const l of porUF.values()) l.sort((a, b) => colar.compare(a.nome, b.nome));
  return { porId, porUF };
}
export const municipiosDaUF = (idx, uf) => (idx && idx.porUF.get(String(uf || '').toUpperCase())) || [];
export const municipio = (idx, id) => (idx && id != null && id !== '' ? idx.porId.get(Number(id)) || null : null);

// Baixa uma vez por página. Falhou? Esquece a promessa, para a próxima
// tentativa baixar de novo em vez de repetir o erro para sempre.
let carregando = null;
export function carregarMunicipios(buscar = (u) => fetch(u)) {
  if (!carregando) {
    carregando = Promise.resolve(buscar(URL_MUNICIPIOS))
      .then(r => { if (!r.ok) throw new Error(`a lista de municípios não carregou (${r.status})`); return r.json(); })
      .then(indexarMunicipios)
      .catch(e => { carregando = null; throw e; });
  }
  return carregando;
}
export function _esquecerMunicipios() { carregando = null; }   // só para os testes

// CEP brasileiro: 8 dígitos, com ou sem ponto e traço (a mesma regra do
// banco, `app.endereco_conferido`: letra no meio não vale).
export const cepValido = v => /^[\d.\-\s]+$/.test(String(v || '').trim()) && String(v).replace(/\D/g, '').length === 8;
export const formatarCep = v => { const d = String(v || '').replace(/\D/g, ''); return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : String(v || ''); };
